// src/stores/UserStore.ts - COMPLETE MERGED VERSION
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './index';
import userService from '@/services/userService';
import {
    UserInfo,
    UserSearchParams,
    UserListResponse,
    Profile,
    UpdateProfileParams,
    ProfileStats
} from '@/services/userService';

/**
 * Complete UserStore for Vietrro Portal
 * Combines user management, profile handling, social features, and validation
 * Compatible with updated UserService and supports multi-language features
 */
export default class UserStore {
    rootStore: RootStore;

    // ===== USER MANAGEMENT STATE =====
    users: UserInfo[] = [];
    selectedUser: UserInfo | null = null;
    userProfiles: Map<string, Profile> = new Map(); // Cache profiles

    // ===== CURRENT USER PROFILE STATE =====
    currentUserProfile: Profile | null = null;
    profileStats: ProfileStats = {
        totalWorks: 0,
        totalLicenses: 0,
        totalRevenue: 0,
        monthlyRevenue: 0,
        profileViews: 0,
        followers: 0,
        following: 0,
        likesReceived: 0,
        commentsReceived: 0,
        downloadsTotal: 0,
        loginCount: 0,
        totalSessionTime: 0,
        profileCompleteness: 0,
        verificationLevel: 'unverified',
        accountStatus: 'pending_verification'
    };

    // ===== SOCIAL FEATURES STATE =====
    followers: UserInfo[] = [];
    following: UserInfo[] = [];
    featuredProfiles: Profile[] = [];
    userActivity: any[] = [];
    userWorks: any[] = [];
    userLicenses: any[] = [];

    // ===== UI STATE =====
    isLoading: boolean = false;
    isUpdating: boolean = false;
    isLoadingSocial: boolean = false;
    isLoadingStats: boolean = false;
    error: string | null = null;
    validationErrors: Record<string, string> = {};

    // ===== PAGINATION STATE =====
    pagination = {
        page: 1,
        pageSize: 20,
        total: 0,
        pageCount: 0
    };

    socialPagination = {
        followers: { page: 1, pageSize: 20, total: 0, pageCount: 0 },
        following: { page: 1, pageSize: 20, total: 0, pageCount: 0 },
        activity: { page: 1, pageSize: 20, total: 0, pageCount: 0 },
        works: { page: 1, pageSize: 20, total: 0, pageCount: 0 },
        licenses: { page: 1, pageSize: 20, total: 0, pageCount: 0 }
    };

    // ===== FORM STATE =====
    isEditing: boolean = false;

    // ===== ADMIN STATE =====
    adminStats: any = null;
    isLoadingAdminStats: boolean = false;

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, { rootStore: false });
        this.rootStore = rootStore;
    }

    // ===== SETTERS =====

    setLoading(loading: boolean) {
        this.isLoading = loading;
    }

    setUpdating(updating: boolean) {
        this.isUpdating = updating;
    }

    setLoadingSocial(loading: boolean) {
        this.isLoadingSocial = loading;
    }

    setLoadingStats(loading: boolean) {
        this.isLoadingStats = loading;
    }

    setError(error: string | null) {
        this.error = error;
    }

    setValidationErrors(errors: Record<string, string>) {
        this.validationErrors = errors;
    }

    setEditing(editing: boolean) {
        this.isEditing = editing;
        if (!editing) {
            this.validationErrors = {};
        }
    }
    
    setLoadingAdminStats(loading: boolean) {
        this.isLoadingAdminStats = loading;
    }

    // ===== USER MANAGEMENT METHODS =====

    /**
     * Fetch users with comprehensive search and filtering
     */
    async fetchUsers(params: UserSearchParams = {}) {
        this.setLoading(true);
        this.setError(null);

        try {
            const response = await userService.searchUsers({
                ...params,
                page: params.page || this.pagination.page,
                pageSize: params.pageSize || this.pagination.pageSize
            });

            runInAction(() => {
                this.users = response.data;

                // Update pagination
                if (response.meta?.pagination) {
                    this.pagination = response.meta.pagination;
                }
            });

            return response;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy danh sách người dùng thất bại';
            });
            console.error('❌ UserStore: Fetch users failed:', err);
            throw err;
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Fetch user by ID with full profile data
     */
    async fetchUserById(documentId: string) {
        this.setLoading(true);
        this.setError(null);

        try {
            const userData = await userService.getUserById(documentId);

            runInAction(() => {
                this.selectedUser = userData;

                // Cache user profile if available
                if (userData.user_profile) {
                    this.userProfiles.set(documentId, userData.user_profile);
                }
            });

            return userData;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy thông tin người dùng thất bại';
            });
            console.error('❌ UserStore: Fetch user by ID failed:', err);
            throw err;
        } finally {
            this.setLoading(false);
        }
    }

    // ===== PROFILE MANAGEMENT METHODS =====

    /**
     * Fetch current user's profile
     */
    async fetchCurrentProfile() {
        this.setLoading(true);
        this.setError(null);

        try {
            const profile = await userService.getCurrentUserProfile();

            runInAction(() => {
                this.currentUserProfile = profile;

                // Update profile stats from profile data
                this.updateStatsFromProfile(profile);
            });

            return profile;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy thông tin profile thất bại';
            });
            console.error('❌ UserStore: Fetch current profile failed:', err);
            throw err;
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Fetch other user's profile by ID
     */
    async fetchUserProfile(userId: string) {
        this.setLoading(true);
        this.setError(null);

        try {
            const profile = await userService.getUserProfile(userId);

            runInAction(() => {
                this.currentUserProfile = profile; // Store as current for viewing
                this.userProfiles.set(userId, profile); // Cache it

                this.updateStatsFromProfile(profile);
            });

            return profile;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy thông tin profile thất bại';
            });
            console.error('❌ UserStore: Fetch user profile failed:', err);
            throw err;
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Update current user's profile with enhanced validation
     */
    async updateCurrentProfile(profileData: UpdateProfileParams) {
        // Enhanced validation using UserService validator
        const validationErrors = userService.validateProfileData
            ? userService.validateProfileData(profileData)
            : this.validateProfileData(profileData);

        this.setValidationErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            throw new Error('Dữ liệu không hợp lệ');
        }

        this.setUpdating(true);
        this.setError(null);

        try {
            const updatedProfile = await userService.updateCurrentUserProfile(profileData);

            runInAction(() => {
                this.currentUserProfile = updatedProfile;
                this.isUpdating = false;
                this.isEditing = false;

                // Update AuthStore with new profile data
                if (this.rootStore.authStore.currentUser) {
                    this.rootStore.authStore.currentUser.firstName = updatedProfile.firstName;
                    this.rootStore.authStore.currentUser.lastName = updatedProfile.lastName;
                    this.rootStore.authStore.currentUser.fullName = updatedProfile.fullName ||
                        (updatedProfile.firstName && updatedProfile.lastName
                            ? `${updatedProfile.firstName} ${updatedProfile.lastName}`
                            : this.rootStore.authStore.currentUser.username);
                    this.rootStore.authStore.currentUser.avatar = updatedProfile.avatar?.url;
                }

                // Update stats
                this.updateStatsFromProfile(updatedProfile);
            });

            return updatedProfile;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Cập nhật profile thất bại';
                this.isUpdating = false;
            });
            console.error('❌ UserStore: Update profile failed:', err);
            throw err;
        }
    }

    // ===== MEDIA UPLOAD METHODS =====

    /**
     * Update avatar using advanced upload helper
     */
    async updateAvatar(file: File) {
        this.setUpdating(true);
        this.setError(null);

        try {
            const result = await userService.updateAvatar(file);

            runInAction(() => {
                if (this.currentUserProfile) {
                    this.currentUserProfile.avatar = result.avatar;
                }
                // Update AuthStore as well
                if (this.rootStore.authStore.currentUser) {
                    this.rootStore.authStore.currentUser.avatar = result.avatar?.url;
                }
                this.isUpdating = false;
            });

            return result;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Upload avatar thất bại';
                this.isUpdating = false;
            });
            console.error('❌ UserStore: Update avatar failed:', err);
            throw err;
        }
    }

    /**
     * Update cover image using advanced upload helper
     */
    async updateCoverImage(file: File) {
        this.setUpdating(true);
        this.setError(null);

        try {
            const result = await userService.updateCoverImage(file);

            runInAction(() => {
                if (this.currentUserProfile) {
                    this.currentUserProfile.coverImage = result.cover;
                }
                this.isUpdating = false;
            });

            return result;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Upload ảnh bìa thất bại';
                this.isUpdating = false;
            });
            console.error('❌ UserStore: Update cover image failed:', err);
            throw err;
        }
    }

    // ===== SOCIAL FEATURES METHODS =====

    /**
     * Follow a user
     */
    async followUser(userId: string) {
        this.setLoadingSocial(true);
        this.setError(null);

        try {
            await userService.followUser(userId);

            runInAction(() => {
                // Update follower count if viewing the followed user's profile
                if (this.currentUserProfile && this.currentUserProfile.id.toString() === userId) {
                    this.currentUserProfile.followers = (this.currentUserProfile.followers || 0) + 1;
                }

                // Update stats
                this.profileStats.following = (this.profileStats.following || 0) + 1;
                this.isLoadingSocial = false;
            });

        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Theo dõi thất bại';
                this.isLoadingSocial = false;
            });
            console.error('❌ UserStore: Follow user failed:', err);
            throw err;
        }
    }

    /**
     * Unfollow a user
     */
    async unfollowUser(userId: string) {
        this.setLoadingSocial(true);
        this.setError(null);

        try {
            await userService.unfollowUser(userId);

            runInAction(() => {
                // Update follower count if viewing the unfollowed user's profile
                if (this.currentUserProfile && this.currentUserProfile.id.toString() === userId) {
                    this.currentUserProfile.followers = Math.max((this.currentUserProfile.followers || 0) - 1, 0);
                }

                // Update stats
                this.profileStats.following = Math.max((this.profileStats.following || 0) - 1, 0);
                this.isLoadingSocial = false;
            });

        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Hủy theo dõi thất bại';
                this.isLoadingSocial = false;
            });
            console.error('❌ UserStore: Unfollow user failed:', err);
            throw err;
        }
    }

    /**
     * Get user's followers
     */
    async fetchUserFollowers(userId: string, page: number = 1, pageSize: number = 20) {
        this.setLoadingSocial(true);
        this.setError(null);

        try {
            const response = await userService.getUserFollowers(userId, page, pageSize);

            runInAction(() => {
                this.followers = response.data;
                this.socialPagination.followers = response.meta?.pagination ||
                    { page, pageSize, total: 0, pageCount: 0 };
                this.isLoadingSocial = false;
            });

            return response;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy danh sách người theo dõi thất bại';
                this.isLoadingSocial = false;
            });
            console.error('❌ UserStore: Fetch followers failed:', err);
            throw err;
        }
    }

    /**
     * Get user's following
     */
    async fetchUserFollowing(userId: string, page: number = 1, pageSize: number = 20) {
        this.setLoadingSocial(true);
        this.setError(null);

        try {
            const response = await userService.getUserFollowing(userId, page, pageSize);

            runInAction(() => {
                this.following = response.data;
                this.socialPagination.following = response.meta?.pagination ||
                    { page, pageSize, total: 0, pageCount: 0 };
                this.isLoadingSocial = false;
            });

            return response;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy danh sách đang theo dõi thất bại';
                this.isLoadingSocial = false;
            });
            console.error('❌ UserStore: Fetch following failed:', err);
            throw err;
        }
    }

    /**
     * Report a user profile
     */
    async reportProfile(userId: string, reason: string, description: string) {
        this.setUpdating(true);
        this.setError(null);

        try {
            await userService.reportProfile(userId, reason, description);

            runInAction(() => {
                this.isUpdating = false;
            });

        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Báo cáo thất bại';
                this.isUpdating = false;
            });
            console.error('❌ UserStore: Report profile failed:', err);
            throw err;
        }
    }

    /**
     * Get featured profiles
     */
    async fetchFeaturedProfiles(page: number = 1, pageSize: number = 10) {
        this.setLoading(true);
        this.setError(null);

        try {
            const response = await userService.getFeaturedProfiles(page, pageSize);

            runInAction(() => {
                this.featuredProfiles = response.data;
            });

            return response;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy profile nổi bật thất bại';
            });
            console.error('❌ UserStore: Fetch featured profiles failed:', err);
            throw err;
        } finally {
            this.setLoading(false);
        }
    }

    // ===== CONTENT & ACTIVITY METHODS =====

    /**
     * Get user's works/content
     */
    async fetchUserWorks(userId: string, page: number = 1, pageSize: number = 20) {
        this.setLoading(true);
        this.setError(null);

        try {
            const response = await userService.getUserWorks(userId, page, pageSize);

            runInAction(() => {
                this.userWorks = response.data;
                this.socialPagination.works = response.meta?.pagination ||
                    { page, pageSize, total: 0, pageCount: 0 };
            });

            return response;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy tác phẩm thất bại';
            });
            console.error('❌ UserStore: Fetch user works failed:', err);
            throw err;
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Get user's licenses
     */
    async fetchUserLicenses(userId?: string, page: number = 1, pageSize: number = 20) {
        this.setLoading(true);
        this.setError(null);

        try {
            const response = await userService.getUserLicenses(userId, page, pageSize);

            runInAction(() => {
                this.userLicenses = response.data;
                this.socialPagination.licenses = response.meta?.pagination ||
                    { page, pageSize, total: 0, pageCount: 0 };
            });

            return response;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy giấy phép thất bại';
            });
            console.error('❌ UserStore: Fetch user licenses failed:', err);
            throw err;
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Get user activity feed
     */
    async fetchUserActivity(userId?: string, page: number = 1, pageSize: number = 20) {
        this.setLoading(true);
        this.setError(null);

        try {
            const response = await userService.getUserActivity(userId, page, pageSize);

            runInAction(() => {
                this.userActivity = response.data;
                this.socialPagination.activity = response.meta?.pagination ||
                    { page, pageSize, total: 0, pageCount: 0 };
            });

            return response;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy hoạt động thất bại';
            });
            console.error('❌ UserStore: Fetch user activity failed:', err);
            throw err;
        } finally {
            this.setLoading(false);
        }
    }

    // ===== STATISTICS METHODS =====

    /**
     * Fetch profile statistics with enhanced error handling
     */
    async fetchProfileStats(userId?: string) {
        this.setLoadingStats(true);

        try {
            const stats = await userService.getProfileStats(userId);

            runInAction(() => {
                this.profileStats = {
                    ...this.profileStats,
                    ...stats
                };
                this.isLoadingStats = false;
            });

            return stats;
        } catch (err: any) {
            runInAction(() => {
                this.isLoadingStats = false;
            });
            console.error('❌ UserStore: Fetch profile stats failed (non-critical):', err);
            // Don't throw error for stats - they're not critical
            return this.profileStats;
        }
    }

    // ===== SETTINGS METHODS =====

    /**
     * Update privacy settings
     */
    async updatePrivacySettings(settings: any) {
        this.setUpdating(true);
        this.setError(null);

        try {
            await userService.updatePrivacySettings(settings);

            runInAction(() => {
                if (this.currentUserProfile) {
                    this.currentUserProfile.privacySettings = settings;
                }
                this.isUpdating = false;
            });

        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Cập nhật cài đặt riêng tư thất bại';
                this.isUpdating = false;
            });
            console.error('❌ UserStore: Update privacy settings failed:', err);
            throw err;
        }
    }

    /**
     * Update notification settings
     */
    async updateNotificationSettings(settings: any) {
        this.setUpdating(true);
        this.setError(null);

        try {
            await userService.updateNotificationSettings(settings);

            runInAction(() => {
                if (this.currentUserProfile) {
                    this.currentUserProfile.notificationSettings = settings;
                }
                this.isUpdating = false;
            });

        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Cập nhật cài đặt thông báo thất bại';
                this.isUpdating = false;
            });
            console.error('❌ UserStore: Update notification settings failed:', err);
            throw err;
        }
    }

    // ===== ADMIN METHODS =====

    /**
     * Get admin user statistics
     */
    async fetchAdminUserStats() {
        this.setLoadingAdminStats(true);
        this.setError(null);

        try {
            const stats = await userService.getAdminUserStats();

            runInAction(() => {
                this.adminStats = stats;
            });

            return stats;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy thống kê admin thất bại';
            });
            console.error('❌ UserStore: Fetch admin stats failed:', err);
            throw err;
        } finally {
            this.setLoadingAdminStats(false);
        }
    }

    /**
     * Change user status (admin function)
     */
    async changeUserStatus(userId: string, status: 'active' | 'suspended' | 'locked' | 'deactivated') {
        this.setUpdating(true);
        this.setError(null);

        try {
            await userService.changeUserStatus(userId, status);

            runInAction(() => {
                // Update user in list if exists
                const userIndex = this.users.findIndex(u => u.id.toString() === userId);
                if (userIndex !== -1 && this.users[userIndex].profile) {
                    this.users[userIndex].profile!.accountStatus = status;
                }

                // Update selected user if it's the same
                if (this.selectedUser && this.selectedUser.id.toString() === userId && this.selectedUser.profile) {
                    this.selectedUser.profile.accountStatus = status;
                }

                this.isUpdating = false;
            });

        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Thay đổi trạng thái người dùng thất bại';
                this.isUpdating = false;
            });
            console.error('❌ UserStore: Change user status failed:', err);
            throw err;
        }
    }

    // ===== VALIDATION METHODS =====

    /**
     * Enhanced profile data validation with multi-language support
     */
    validateProfileData(data: Partial<UpdateProfileParams>): Record<string, string> {
        const errors: Record<string, string> = {};

        // Basic field validations
        if (data.firstName !== undefined && data.firstName && data.firstName.length > 50) {
            errors.firstName = 'Tên không được vượt quá 50 ký tự';
        }

        if (data.lastName !== undefined && data.lastName && data.lastName.length > 50) {
            errors.lastName = 'Họ không được vượt quá 50 ký tự';
        }

        if (data.displayName !== undefined && data.displayName && data.displayName.length > 100) {
            errors.displayName = 'Tên hiển thị không được vượt quá 100 ký tự';
        }

        if (data.bio !== undefined && data.bio && data.bio.length > 1000) {
            errors.bio = 'Tiểu sử không được vượt quá 1000 ký tự';
        }

        // Website validation
        if (data.website !== undefined && data.website && data.website.trim()) {
            if (data.website.length > 255) {
                errors.website = 'Website không được vượt quá 255 ký tự';
            }
            const urlRegex = /^https?:\/\/.+\..+/;
            if (!urlRegex.test(data.website)) {
                errors.website = 'Website phải có định dạng hợp lệ (bắt đầu với http:// hoặc https://)';
            }
        }

        // Phone number validation
        if (data.phoneNumber !== undefined && data.phoneNumber && data.phoneNumber.length > 20) {
            errors.phoneNumber = 'Số điện thoại không được vượt quá 20 ký tự';
        }

        // Professional fields validation
        if (data.occupation !== undefined && data.occupation && data.occupation.length > 100) {
            errors.occupation = 'Nghề nghiệp không được vượt quá 100 ký tự';
        }

        if (data.company !== undefined && data.company && data.company.length > 100) {
            errors.company = 'Công ty không được vượt quá 100 ký tự';
        }

        if (data.jobTitle !== undefined && data.jobTitle && data.jobTitle.length > 100) {
            errors.jobTitle = 'Chức vụ không được vượt quá 100 ký tự';
        }

        // Date validation
        if (data.dateOfBirth !== undefined && data.dateOfBirth) {
            const date = new Date(data.dateOfBirth);
            if (isNaN(date.getTime())) {
                errors.dateOfBirth = 'Ngày sinh không hợp lệ';
            } else if (date > new Date()) {
                errors.dateOfBirth = 'Ngày sinh không thể là tương lai';
            }
        }

        // Additional validations for international fields
        if (data.nationality !== undefined && data.nationality && data.nationality.length > 100) {
            errors.nationality = 'Quốc tịch không được vượt quá 100 ký tự';
        }

        // Language validation
        if (data.language !== undefined && data.language) {
            const supportedLanguages = ['vi', 'en', 'zh', 'ja', 'ko'];
            if (!supportedLanguages.includes(data.language)) {
                errors.language = 'Ngôn ngữ không được hỗ trợ';
            }
        }

        // Currency validation
        if (data.currency !== undefined && data.currency) {
            const supportedCurrencies = ['VND', 'USD', 'EUR'];
            if (!supportedCurrencies.includes(data.currency)) {
                errors.currency = 'Tiền tệ không được hỗ trợ';
            }
        }

        // Gender validation
        if (data.gender !== undefined && data.gender) {
            const validGenders = ['male', 'female', 'other', 'prefer_not_to_say'];
            if (!validGenders.includes(data.gender)) {
                errors.gender = 'Giới tính không hợp lệ';
            }
        }

        // Experience level validation
        if (data.experience !== undefined && data.experience) {
            const validExperience = ['entry_level', 'mid_level', 'senior_level', 'expert'];
            if (!validExperience.includes(data.experience)) {
                errors.experience = 'Cấp độ kinh nghiệm không hợp lệ';
            }
        }

        return errors;
    }

    // ===== COMPUTED VALUES =====

    get isProfileComplete() {
        if (!this.currentUserProfile) return false;

        const profile = this.currentUserProfile;
        const importantFields = ['firstName', 'lastName', 'bio'];

        return importantFields.every(field =>
            profile[field as keyof Profile] &&
            String(profile[field as keyof Profile]).trim().length > 0
        );
    }

    get profileCompletionPercentage() {
        if (!this.currentUserProfile) return 0;

        const fields = [
            'firstName', 'lastName', 'bio', 'avatar', 'website',
            'dateOfBirth', 'socialLinks', 'occupation', 'company', 'jobTitle',
            'phoneNumber', 'nationality', 'language'
        ];

        const completedFields = fields.filter(field => {
            const value = this.currentUserProfile![field as keyof Profile];
            if (field === 'socialLinks') {
                return value && typeof value === 'object' && Object.keys(value).length > 0;
            }
            return value && String(value).trim().length > 0;
        });

        return Math.round((completedFields.length / fields.length) * 100);
    }

    get displayName() {
        if (!this.currentUserProfile) return '';

        if (this.currentUserProfile.displayName) {
            return this.currentUserProfile.displayName;
        }

        if (this.currentUserProfile.firstName && this.currentUserProfile.lastName) {
            return `${this.currentUserProfile.firstName} ${this.currentUserProfile.lastName}`;
        }

        return this.rootStore.authStore.currentUser?.username || '';
    }

    get fullName() {
        if (!this.currentUserProfile) return '';

        if (this.currentUserProfile.fullName) {
            return this.currentUserProfile.fullName;
        }

        if (this.currentUserProfile.firstName && this.currentUserProfile.lastName) {
            return `${this.currentUserProfile.firstName} ${this.currentUserProfile.lastName}`;
        }

        return this.rootStore.authStore.currentUser?.username || '';
    }

    get totalUsers() {
        return this.pagination.total;
    }

    get hasMorePages() {
        return this.pagination.page < this.pagination.pageCount;
    }

    get currentPageInfo() {
        return {
            page: this.pagination.page,
            pageSize: this.pagination.pageSize,
            total: this.pagination.total,
            totalPages: this.pagination.pageCount,
            hasNext: this.hasMorePages,
            hasPrev: this.pagination.page > 1
        };
    }

    get isOwnProfile() {
        if (!this.currentUserProfile || !this.rootStore.authStore.currentUser) {
            return false;
        }

        return this.currentUserProfile.id === this.rootStore.authStore.currentUser.id;
    }

    get profileCompletionStatus() {
        const percentage = this.profileCompletionPercentage;

        if (percentage >= 90) return { status: 'excellent', color: 'green', message: 'Profile xuất sắc!' };
        if (percentage >= 70) return { status: 'good', color: 'blue', message: 'Profile tốt' };
        if (percentage >= 50) return { status: 'average', color: 'yellow', message: 'Profile trung bình' };
        if (percentage >= 30) return { status: 'poor', color: 'orange', message: 'Profile cần cải thiện' };
        return { status: 'incomplete', color: 'red', message: 'Profile chưa hoàn chỉnh' };
    }

    get missingRequiredFields() {
        if (!this.currentUserProfile) return [];

        const requiredFields = [
            { field: 'firstName', label: 'Tên' },
            { field: 'lastName', label: 'Họ' },
            { field: 'bio', label: 'Tiểu sử' }
        ];

        return requiredFields.filter(({ field }) => {
            const value = this.currentUserProfile![field as keyof Profile];
            return !value || String(value).trim().length === 0;
        });
    }

    get canEditProfile() {
        return this.isOwnProfile || this.rootStore.authStore.userRoles?.includes('admin');
    }

    get verificationBadgeInfo() {
        if (!this.currentUserProfile) {
            return { type: 'none', label: 'Chưa xác minh', color: '#999999' };
        }

        return userService.getVerificationBadgeInfo
            ? userService.getVerificationBadgeInfo(this.currentUserProfile)
            : this.getLocalVerificationBadgeInfo();
    }

    get accountStatusInfo() {
        if (!this.currentUserProfile) {
            return { label: 'Chờ xác minh', color: '#FAAD14', icon: '⏳' };
        }

        return userService.getAccountStatusInfo
            ? userService.getAccountStatusInfo(this.currentUserProfile)
            : this.getLocalAccountStatusInfo();
    }

    get userLanguagePreference() {
        return this.currentUserProfile?.language || 'vi';
    }

    get userCurrencyPreference() {
        return this.currentUserProfile?.currency || 'VND';
    }

    get userThemePreference() {
        return this.currentUserProfile?.theme || 'light';
    }

    // ===== HELPER METHODS =====

    /**
     * Update stats from profile data
     */
    private updateStatsFromProfile(profile: Profile) {
        this.profileStats = {
            ...this.profileStats,
            profileCompleteness: profile.profileCompleteness || this.profileCompletionPercentage,
            verificationLevel: profile.verificationLevel || 'unverified',
            accountStatus: profile.accountStatus || 'pending_verification',
            loginCount: profile.loginCount || 0,
            totalSessionTime: profile.totalSessionTime || 0,
            followers: profile.followers || 0,
            following: profile.following || 0,
            totalWorks: profile.totalWorks || 0,
            likesReceived: profile.likesReceived || 0,
            lastActiveAt: profile.lastActiveAt
        };
    }

    /**
     * Get user profile from cache or fetch
     */
    async getUserProfile(userId: string): Promise<Profile | null> {
        // Check cache first
        if (this.userProfiles.has(userId)) {
            return this.userProfiles.get(userId)!;
        }

        try {
            const profile = await userService.getUserProfile(userId);
            this.userProfiles.set(userId, profile);
            return profile;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            return null;
        }
    }

    /**
     * Get formatted display name for any user
     */
    getFormattedDisplayName(user: UserInfo | Profile): string {
        if (userService.formatUserDisplayName) {
            return userService.formatUserDisplayName(user);
        }

        // Fallback implementation
        if ('displayName' in user && user.displayName) {
            return user.displayName;
        }

        if (user.firstName || user.lastName) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim();
        }

        if ('username' in user) {
            return user.username;
        }

        return 'Người dùng';
    }

    /**
     * Get user avatar URL with fallback
     */
    getUserAvatarUrl(user: UserInfo | Profile): string {
        if (userService.getUserAvatarUrl) {
            return userService.getUserAvatarUrl(user);
        }

        // Fallback implementation
        const avatar = user.avatar;

        if (typeof avatar === 'string') {
            return avatar.startsWith('http') ? avatar : `${import.meta.env.VITE_API_URL}${avatar}`;
        }

        if (avatar?.url) {
            return avatar.url.startsWith('http') ? avatar.url : `${import.meta.env.VITE_API_URL}${avatar.url}`;
        }

        return '/assets/images/default-avatar.png';
    }

    /**
     * Get user cover image URL with fallback
     */
    getUserCoverUrl(user: UserInfo | Profile): string {
        if (userService.getUserCoverUrl) {
            return userService.getUserCoverUrl(user);
        }

        // Fallback implementation
        const cover = user.coverImage;

        if (typeof cover === 'string') {
            return cover.startsWith('http') ? cover : `${import.meta.env.VITE_API_URL}${cover}`;
        }

        if (cover?.url) {
            return cover.url.startsWith('http') ? cover.url : `${import.meta.env.VITE_API_URL}${cover.url}`;
        }

        return '/assets/images/default-cover.jpg';
    }

    /**
     * Local verification badge info (fallback)
     */
    private getLocalVerificationBadgeInfo() {
        if (!this.currentUserProfile) {
            return { type: 'none', label: 'Chưa xác minh', color: '#999999' };
        }

        switch (this.currentUserProfile.verificationLevel) {
            case 'fully_verified':
                return { type: 'gold', label: 'Được xác minh đầy đủ', color: '#FFD700' };
            case 'document_verified':
                return { type: 'blue', label: 'Được xác minh giấy tờ', color: '#1890FF' };
            case 'phone_verified':
                return { type: 'silver', label: 'Được xác minh số điện thoại', color: '#C0C0C0' };
            case 'email_verified':
                return { type: 'bronze', label: 'Được xác minh email', color: '#CD7F32' };
            default:
                return { type: 'none', label: 'Chưa xác minh', color: '#999999' };
        }
    }

    /**
     * Local account status info (fallback)
     */
    private getLocalAccountStatusInfo() {
        if (!this.currentUserProfile) {
            return { label: 'Chờ xác minh', color: '#FAAD14', icon: '⏳' };
        }

        switch (this.currentUserProfile.accountStatus) {
            case 'active':
                return { label: 'Đang hoạt động', color: '#52C41A', icon: '✅' };
            case 'suspended':
                return { label: 'Bị tạm ngưng', color: '#FF4D4F', icon: '⏸️' };
            case 'locked':
                return { label: 'Bị khóa', color: '#FF7A45', icon: '🔒' };
            case 'deactivated':
                return { label: 'Đã vô hiệu hóa', color: '#999999', icon: '💤' };
            default:
                return { label: 'Chờ xác minh', color: '#FAAD14', icon: '⏳' };
        }
    }

    /**
     * Format currency amount using user preference
     */
    formatCurrency(amount: number): string {
        const currency = this.userCurrencyPreference;

        if (userService.formatCurrency) {
            return userService.formatCurrency(amount, currency);
        }

        // Fallback implementation
        switch (currency) {
            case 'VND':
                return new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND'
                }).format(amount);
            case 'USD':
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }).format(amount);
            case 'EUR':
                return new Intl.NumberFormat('de-DE', {
                    style: 'currency',
                    currency: 'EUR'
                }).format(amount);
            default:
                return amount.toLocaleString();
        }
    }

    // ===== UTILITY METHODS =====

    /**
     * Clear error state
     */
    clearError() {
        this.error = null;
        this.validationErrors = {};
    }

    /**
     * Clear selected user
     */
    clearSelectedUser() {
        this.selectedUser = null;
    }

    /**
     * Clear current profile
     */
    clearCurrentProfile() {
        this.currentUserProfile = null;
        this.profileStats = {
            totalWorks: 0,
            totalLicenses: 0,
            totalRevenue: 0,
            monthlyRevenue: 0,
            profileViews: 0,
            followers: 0,
            following: 0,
            likesReceived: 0,
            commentsReceived: 0,
            downloadsTotal: 0,
            loginCount: 0,
            totalSessionTime: 0,
            profileCompleteness: 0,
            verificationLevel: 'unverified',
            accountStatus: 'pending_verification'
        };
    }

    /**
     * Clear social data
     */
    clearSocialData() {
        this.followers = [];
        this.following = [];
        this.featuredProfiles = [];
        this.userActivity = [];
        this.userWorks = [];
        this.userLicenses = [];

        this.socialPagination = {
            followers: { page: 1, pageSize: 20, total: 0, pageCount: 0 },
            following: { page: 1, pageSize: 20, total: 0, pageCount: 0 },
            activity: { page: 1, pageSize: 20, total: 0, pageCount: 0 },
            works: { page: 1, pageSize: 20, total: 0, pageCount: 0 },
            licenses: { page: 1, pageSize: 20, total: 0, pageCount: 0 }
        };
    }

    /**
     * Reset store to initial state
     */
    reset() {
        this.users = [];
        this.selectedUser = null;
        this.userProfiles.clear();
        this.clearCurrentProfile();
        this.clearSocialData();
        this.adminStats = null;

        this.isLoading = false;
        this.isUpdating = false;
        this.isLoadingSocial = false;
        this.isLoadingStats = false;
        this.isLoadingAdminStats = false;
        this.error = null;
        this.validationErrors = {};
        this.isEditing = false;

        this.pagination = {
            page: 1,
            pageSize: 20,
            total: 0,
            pageCount: 0
        };
    }

    // ===== SEARCH & FILTER HELPERS =====

    /**
     * Search users with debouncing support
     */
    private searchTimeout: NodeJS.Timeout | null = null;

    async searchUsersDebounced(query: string, delay: number = 300) {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        return new Promise<UserListResponse>((resolve, reject) => {
            this.searchTimeout = setTimeout(async () => {
                try {
                    const result = await this.fetchUsers({ query, page: 1 });
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            }, delay);
        });
    }

    /**
     * Filter users by criteria
     */
    filterUsers(criteria: {
        accountStatus?: string;
        verificationLevel?: string;
        hasAvatar?: boolean;
        dateRange?: { start: string; end: string };
    }) {
        return this.fetchUsers({
            accountStatus: criteria.accountStatus,
            verificationLevel: criteria.verificationLevel,
            hasAvatar: criteria.hasAvatar,
            createdAfter: criteria.dateRange?.start,
            createdBefore: criteria.dateRange?.end,
            page: 1
        });
    }

    // ===== LOCALIZATION HELPERS =====

    /**
     * Get supported languages from service
     */
    get supportedLanguages() {
        return userService.getSupportedLanguages
            ? userService.getSupportedLanguages()
            : [
                { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
                { code: 'en', name: 'English', nativeName: 'English' }
            ];
    }

    /**
     * Get supported currencies from service
     */
    get supportedCurrencies() {
        return userService.getSupportedCurrencies
            ? userService.getSupportedCurrencies()
            : [
                { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
                { code: 'USD', name: 'US Dollar', symbol: '$' },
                { code: 'EUR', name: 'Euro', symbol: '€' }
            ];
    }
}
